import { EditAbsenceModal } from "@/modals/users/edit-absence-modal";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import {
	flip,
	offset,
	shift,
	useClientPoint,
	useFloating,
	useHover,
	useInteractions,
} from "@msuite/ui/libs/floating";
import { cn } from "@msuite/ui/utils";
import { getColorByString } from "@msuite/utils/colors";
import type { AbsenceListItem } from "@msuite/utils/types";
import { Fragment, memo, useMemo, useState } from "react";
import { AbsencesCalendarBlockPopover } from "./absences-calendar-block-popover";
import { useAbsencesCalendarContext } from "./context";

/** Props Interface */
interface AbsencesCalendarBlockProps {
	absence: Omit<AbsenceListItem, "consumedDays">;
	gridColStart: number;
	gridColEnd: number;
	overflowLeft?: boolean;
	overflowRight?: boolean;
}

interface BlockBodyProps extends AbsencesCalendarBlockProps {
	hoverProps?: Record<string, unknown>;
	innerRef?: (node: HTMLDivElement | null) => void;
	onClick: () => void;
	onMouseEnter?: () => void;
}

function BlockBody({
	absence,
	gridColStart,
	gridColEnd,
	overflowLeft,
	overflowRight,
	hoverProps,
	innerRef,
	onClick,
	onMouseEnter,
}: BlockBodyProps) {
	/** Context */
	const { controller } = useAbsencesCalendarContext();

	/** Memoized */
	const style = useMemo(
		() => ({
			gridColumn: `${gridColStart} / ${gridColEnd}`,
			backgroundColor: `var(--color-${getColorByString(absence.category)}-500)`,
		}),
		[gridColStart, gridColEnd, absence.category],
	);

	/** Render */
	return (
		<div
			ref={innerRef}
			{...hoverProps}
			onClick={onClick}
			onMouseEnter={onMouseEnter}
			className={cn(
				"pointer-events-auto my-1",
				"flex flex-row items-center px-3 overflow-hidden gap-0.5",
				"cursor-pointer",
				!overflowLeft && !overflowRight && "rounded",
				overflowLeft && !overflowRight && "rounded-r",
				!overflowLeft && overflowRight && "rounded-l",
			)}
			style={style}
		>
			{overflowLeft && (
				<div
					className="pr-3"
					onClick={(e) => {
						e.stopPropagation();
						controller.onBack();
					}}
				>
					<Icon
						name="ArrowLeft"
						className="size-3 text-white shrink-0"
					/>
				</div>
			)}
			<span className="text-xs text-white truncate">{absence.name}</span>
			{overflowRight && (
				<div
					className="pl-3 ml-auto"
					onClick={(e) => {
						e.stopPropagation();
						controller.onForward();
					}}
				>
					<Icon
						name="ArrowRight"
						className="size-3 text-white shrink-0"
					/>
				</div>
			)}
		</div>
	);
}

function ArmedBlock(props: AbsencesCalendarBlockProps & { onClick: () => void }) {
	/** Hooks */
	const popoverDisclosure = useDisclosure();
	const { refs, context, floatingStyles } = useFloating({
		open: popoverDisclosure.isOpen,
		onOpenChange: popoverDisclosure.setIsOpen,
		middleware: [offset(10), shift({ padding: 10 }), flip()],
	});
	const hover = useHover(context);
	const clientPoint = useClientPoint(context, { axis: "x" });
	const { getReferenceProps, getFloatingProps } = useInteractions([hover, clientPoint]);

	/** Render */
	return (
		<Fragment>
			<BlockBody
				{...props}
				innerRef={refs.setReference}
				hoverProps={getReferenceProps()}
			/>
			{popoverDisclosure.isOpen && (
				<AbsencesCalendarBlockPopover
					absence={props.absence}
					setFloating={refs.setFloating}
					getFloatingProps={getFloatingProps}
					floatingStyles={floatingStyles}
				/>
			)}
		</Fragment>
	);
}

function AbsencesCalendarBlockImpl(props: AbsencesCalendarBlockProps) {
	/** Hooks */
	const disclosure = useDisclosure();

	/** State */
	const [armed, setArmed] = useState(false);

	/** Guard */
	if (props.absence.state === "cancelled") return null;
	if (props.absence.state === "rejected") return null;

	/** Render */
	return (
		<Fragment>
			{armed ? (
				<ArmedBlock
					{...props}
					onClick={disclosure.onOpen}
				/>
			) : (
				<BlockBody
					{...props}
					onClick={disclosure.onOpen}
					onMouseEnter={() => setArmed(true)}
				/>
			)}
			{disclosure.isOpen && (
				<EditAbsenceModal
					isOpen={disclosure.isOpen}
					onClose={disclosure.onClose}
					absenceId={props.absence.id}
				/>
			)}
		</Fragment>
	);
}

export const AbsencesCalendarBlock = memo(AbsencesCalendarBlockImpl);
